import { observable, computed, action, makeObservable } from 'mobx';
import { DominosRootStore } from './index';
import { APP_CONFIG } from '../base';
import { goFish } from '../api';
import { showInfoPopup, subscribeToClose } from '../../../modules/InfoPopup';
import { showOverlay } from '../../../modules/Popup';
import { setConfigPCP } from '../../../modules/PopupChangePlace';
import { decodeB64Unicode } from "../../utils/convert";
import cookies from "../../utils/storage/cookies";
import { getAppDefaultMessage } from "../dictionary";
import { isOrderStatus } from '../navigation';
import { insiderLocalEvents } from '../../utils/insiderLocalEvents';


const AID_COOKIE = 'aid';
const D_COOKIE = 'delivery';

const getDeliveryTypeFromCookie = () => {
    const d = cookies.read(D_COOKIE);
    return d && parseInt(d) === 1 ? 1 : 0
}

export class LocalStore {
    dominosStore: DominosRootStore;

    /**
     * This id is used by menu view.
     * Can't be undefined so default is set when there is no current local
     *
     * @type {string}
     * @memberof LocalStore
     */
    @observable localId: string;

    /**
     * Current local if set
     *
     * @type {LocalMapData}
     * @memberof LocalStore
     */
    @observable local: LocalMapData;

    /**
     * Current delivery type
     *
     * @type {DeliveryType}
     * @memberof LocalStore
     */
    @observable deliveryType: DeliveryType = getDeliveryTypeFromCookie();

    /**
     * Delivery address used by the user before
     *
     * @type {AddressData}
     * @memberof LocalStore
     */
    @observable deliveryAddress: AddressData;

    /**
     *
     *
     * @type {false}
     * @memberof LocalStore
     */
    priceChanged = false;

    constructor(store: DominosRootStore) {
        makeObservable(this);
        this.dominosStore = store;

    }

    /**
     * Update local data in store.
     * All data has to be validated before this function.
     *
     * @param {LocalMapData} local
     * @memberof LocalStore
     */
    @action udpateCurrentLocal = (local: LocalMapData, deliveryType: DeliveryType) => {
        this.local = { ...local };
        this.localId = local.Id;
        this.deliveryType = deliveryType;
        this.checkAddress();
    };

    /**
     * Current local inactive message.
     * Local is closed or temporary unavailable.
     *
     * @readonly
     * @memberof LocalStore
     */
    @computed({ keepAlive: true })
    get localMessage() {
        return (this.localIsInactive && this.local.InactiveMessage) || '';
    }

    /**
     * Local is set and is inactive
     *
     * @readonly
     * @memberof LocalStore
     */
    @computed get localIsInactive() {
        return this.local && !this.local.IsActive;
    }

    @computed get minimalValue() {
        return this.local && this.local.MinimalOrderValue;
    }

    /**
     * This means that local is not set and default will be used on menu view.
     *
     * @readonly
     * @memberof LocalStore
     */
    @computed get isLocalNotSet(): boolean {
        return this.localId && typeof this.local == 'undefined';
    }

    /**
     * If local is not set we can't modify cart.
     * This is trigger for popupChangePlace
     *
     * @returns {boolean}
     * @memberof LocalStore
     */
    checkLocalNotReady = (): boolean => {
        if (this.isLocalNotSet) {
            setConfigPCP({ header: 'prices' });
            showOverlay('localchange');
            return true;
        } else {
            return false;
        }
    };

    /**
     * Set current local in store after getting it from back-end.
     * If none is set just update localId by default value.
     * This is needed on menu and promotion view.
     *
     * @memberof LocalStore
     */
    getCurrentLocal = async () => {
        try {
            const response: LocalData = await goFish({ api: 'getcurrent' });
            // Throw error if undefined so default localId will be set
            if (response.Data.Id) {
                this.udpateCurrentLocal(response.Data, getDeliveryTypeFromCookie());
            }
        } catch (e) {
            this.localId = APP_CONFIG.local?.default || '';
            this.checkAddress();
        }
    };

    /**
     * Main setter for current local.
     * Handles price change popup.
     * Handles beta popup.
     *
     * @memberof LocalStore
     */
    setCurrentLocal = (LocalId: string, DeliveryType: DeliveryType): Promise<LocalData> => {
        const data: UserLocal = { LocalId, Force: false, DeliveryType };
        // Reset
        this.priceChanged = false;
        // Set local
        return goFish({
            api: 'setcurrent',
            data: data
        }).then(
            (response: LocalData) => {
                // Local is set
                if (response.Status) {
                    // Save in store
                    this.udpateCurrentLocal(response.Data, DeliveryType);
                    insiderLocalEvents(DeliveryType, LocalId, response.Data?.City)
                    return response;
                }
                // Local is not set for some reason
                else {
                    // Change comming to local outside beta
                    // TODO schema is not updated, remove after BETA period
                    if (
                        response.Code === 'dominos.errors.redirecttooldservice' &&
                        response.RedirectData &&
                        response.RedirectData.Url
                    ) {
                        // Address needed to fill in in partial
                        if (response.RedirectData.LocalAddress) {
                            sessionStorage.setItem(
                                'PSVLocalAddress',
                                response.RedirectData.LocalAddress
                            );
                        }
                        // Show partial
                        showInfoPopup({ partial: 'betaexitpopup' });
                        // W8 for user reaction
                        return new Promise((resolve, reject) => {
                            subscribeToClose(p => {
                                sessionStorage.removeItem('PSVLocalAddress');
                                // Confirmed so redirect with old fashion form post
                                if (p && p.forced) {
                                    const form = document.createElement('form');
                                    form.action = response.RedirectData.Url;
                                    form.method = 'post';
                                    for (let k in response.RedirectData.PostData) {
                                        const input = document.createElement('input');
                                        input.type = 'text';
                                        input.value = response.RedirectData.PostData[k];
                                        input.name = k;
                                        form.appendChild(input);
                                    }
                                    document.body.appendChild(form);
                                    form.submit();
                                }
                                // User declined so basicaly do nothing
                                else {
                                    reject({
                                        Message: '',
                                        Status: false,
                                        Code: 'dominos.errors.redirecttooldservice'
                                    });
                                }
                            });
                        });
                    }
                    // Price info, user has to accept
                    else if (response.Code === this.dominosStore.CODE_PRICE_CHANGED) {
                        // Show popup where decision is obligatory
                        showInfoPopup({ partial: 'cartupdate', url: `?Id=${LocalId}` });

                        return new Promise((resolve, reject) => {
                            // Listen for user response
                            subscribeToClose(async p => {
                                if (p && p.forced) {
                                    // Change accepted
                                    const forceResp = await this.forceCurrentLocal(
                                        LocalId,
                                        DeliveryType
                                    );
                                    // Local is set
                                    if (forceResp.Status) {
                                        // Save in store
                                        this.udpateCurrentLocal(forceResp.Data, DeliveryType);
                                        insiderLocalEvents(DeliveryType, LocalId, forceResp.Data?.City)
                                        // Update flag
                                        this.priceChanged = true;
                                        // Return response with 'price changed' Code
                                        // normally this is false code
                                        // in this case we want to know that user accepted changes
                                        resolve({
                                            ...forceResp,
                                            Code: this.dominosStore.CODE_PRICE_CHANGED
                                        });
                                    }
                                    // Return response with back-end message/code
                                    else {
                                        resolve({ ...forceResp });
                                    }
                                } else {
                                    // User declined so basicaly do nothing
                                    reject({
                                        Message: '',
                                        Status: false,
                                        Code: this.dominosStore.CODE_PRICE_CHANGED
                                    });
                                }
                                window.location.reload();
                            });
                        });
                    }
                    // Something is wrong so just display message
                    else {
                        return response;
                    }
                }
            },
            (err: StandardResponse) => {
                if (err.Code === '500') {
                    return this.handleLocal500();
                }
                return { ...err, Data: undefined };
            }
        );
    };

    /**
     * Will ignore price change
     *
     * @memberof LocalStore
     */
    forceCurrentLocal = (LocalId: string, DeliveryType: DeliveryType): Promise<LocalData> => {
        const data: UserLocal = { LocalId, Force: true, DeliveryType };
        // Set local
        return goFish({
            api: 'setcurrent',
            data: data
        }).then(
            res => res,
            (err: StandardResponse) => {
                if (err.Code === '500') {
                    return this.handleLocal500();
                }
                return { ...err, Data: undefined };
            }
        );
    };

    /**
     * Update address typed by the user.
     * @param address
     */
    @action saveAddress = (address: AddressData) => {
        // FlatNr is not required
        if (address?.City && address.Street && address.HouseNr) {
            this.deliveryAddress = { ...address };
        }
    };

    checkAddress = () => {
        try {
            const aid: AddressData = JSON.parse(decodeB64Unicode(cookies.read(AID_COOKIE)));
            this.saveAddress(aid);
        } catch (e) {}
    };

    /**
     * Special case when we don't know if local was set or not
     */
    handleLocal500 = async (): Promise<LocalData> => {
        return new Promise((resolve, reject) => {
            showInfoPopup({ text: getAppDefaultMessage('errors.server') });
            subscribeToClose(() => window.location.reload());
        });
    };
}
